import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { TemplateIcon } from '../utils/iconResolver';
import { getWorkerIcon } from '../utils/workerIcons';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import UniversalExportModal from './UniversalExportModal';
import AIOutputNotice from './AIOutputNotice';

import {
  ArrowClockwise,
  ArrowRight,
  ArrowSquareOut,
  ChatText,
  CheckCircle,
  CircleNotch,
  Clock,
  DownloadSimple,
  File,
  FileText,
  FileXls,
  GearSix,
  Globe,
  Headphones,
  Image,
  Lightning,
  Link as LinkIcon,
  Microphone,
  MusicNote,
  Play,
  Sparkle,
  Trash,
  UploadSimple,
  Video,
  WarningCircle,
  WifiHigh,
  X
} from '@phosphor-icons/react';
/**
 * RunAgentModal — Universal Schema-Driven Modal (P0 SPEC)
 * 
 * RULES:
 * - Render inputs based ONLY on accepted_inputs
 * - Render required inputs based on required_inputs
 * - NEVER infer input types from agent profile
 * - NEVER hard-code logic for specific agents
 * - ONE text field only (Notes or Instructions)
 * - NO Schedule button inside modal
 * 
 * MODES:
 * - FIXED-TASK MODE: Template instances (is_template_instance || source_template_id || fixed_intent)
 * - OPEN MODE: Custom agents
 * 
 * SPECIAL CASE: voice_input_agent (Meeting Agents)
 * - These agents run AUTOMATICALLY when meetings occur
 * - The Run Modal is INFORMATIONAL ONLY - no chat input
 * - Shows agent description, status, and link to manage integrations
 * 
 * SUPPORTED INPUT FORMATS:
 * - Legacy: 'text', 'pdf', 'image', 'audio', 'webhook', etc.
 * - New (Builder V2): 'input.text', 'input.file', 'input.image', 'input.audio', 'input.webhook_payload', etc.
 */

/**
 * Normalize input type to handle both legacy and new builder formats
 * Maps new 'input.*' format to legacy equivalents for consistent processing
 */
const normalizeInputType = (inputType) => {
  if (!inputType) return null;
  
  // New builder format mapping (input.* -> legacy)
  const newFormatMap = {
    'input.text': 'text',
    'input.file': 'file',
    'input.image': 'image',
    'input.audio': 'audio',
    'input.video': 'video',
    'input.webhook_payload': 'webhook',
    'input.receive_webhook': 'webhook',
    'receive_webhook': 'webhook',
    'input.url': 'url',
    'input.json': 'json',
    'input.pdf': 'pdf',
    'input.docx': 'docx',
    'input.xlsx': 'xlsx',
    'input.csv': 'csv',
  };
  
  // Check if it's a new format that needs mapping
  if (newFormatMap[inputType]) {
    return newFormatMap[inputType];
  }
  
  // Handle generic input.* format by stripping prefix
  if (inputType.startsWith('input.')) {
    return inputType.replace('input.', '');
  }
  
  return inputType;
};

/**
 * Normalize an array of input types
 */
const normalizeInputTypes = (inputTypes) => {
  if (!Array.isArray(inputTypes)) return [];
  return inputTypes.map(normalizeInputType).filter(Boolean);
};

// Supported input type configurations - Enhanced with modern styling
const INPUT_CONFIG = {
  // Text input
  text: { label: 'Text', icon: ChatText, color: 'blue', gradient: 'from-blue-500 to-cyan-500' },
  // Document types
  pdf: { label: 'PDF', accept: '.pdf', icon: FileText, color: 'red', gradient: 'from-red-500 to-rose-500' },
  docx: { label: 'Word', accept: '.docx,.doc', icon: FileText, color: 'blue', gradient: 'from-blue-500 to-indigo-500' },
  xlsx: { label: 'Excel', accept: '.xlsx,.xls', icon: FileXls, color: 'green', gradient: 'from-green-500 to-emerald-500' },
  csv: { label: 'CSV', accept: '.csv', icon: FileXls, color: 'emerald', gradient: 'from-emerald-500 to-teal-500' },
  file: { label: 'File', accept: '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt', icon: File, color: 'slate', gradient: 'from-slate-500 to-gray-500' },
  file_pdf: { label: 'PDF', accept: '.pdf', icon: FileText, color: 'red', gradient: 'from-red-500 to-rose-500' },
  file_docx: { label: 'Word', accept: '.docx,.doc', icon: FileText, color: 'blue', gradient: 'from-blue-500 to-indigo-500' },
  file_xlsx: { label: 'Excel', accept: '.xlsx,.xls', icon: FileXls, color: 'green', gradient: 'from-green-500 to-emerald-500' },
  file_excel: { label: 'Excel', accept: '.xlsx,.xls', icon: FileXls, color: 'green', gradient: 'from-green-500 to-emerald-500' },
  excel: { label: 'Excel', accept: '.xlsx,.xls', icon: FileXls, color: 'green', gradient: 'from-green-500 to-emerald-500' },
  excel_file: { label: 'Excel', accept: '.xlsx,.xls', icon: FileXls, color: 'green', gradient: 'from-green-500 to-emerald-500' },
  file_csv: { label: 'CSV', accept: '.csv', icon: FileXls, color: 'emerald', gradient: 'from-emerald-500 to-teal-500' },
  spreadsheet: { label: 'Spreadsheet', accept: '.xlsx,.xls,.csv', icon: FileXls, color: 'green', gradient: 'from-green-500 to-emerald-500' },
  // Image
  image: { label: 'Image', accept: '.jpg,.jpeg,.png,.gif,.webp', icon: Image, color: 'pink', gradient: 'from-pink-500 to-rose-500' },
  file_image: { label: 'Image', accept: '.jpg,.jpeg,.png,.gif,.webp', icon: Image, color: 'pink', gradient: 'from-pink-500 to-rose-500' },
  // Audio
  audio: { label: 'Audio', accept: '.mp3,.wav,.m4a,.ogg,.webm,.aac', icon: Microphone, color: 'teal', gradient: 'from-[#00E5C0] to-[#00B4D8]' },
  audio_file: { label: 'Audio', accept: '.mp3,.wav,.m4a,.ogg,.webm,.aac', icon: Microphone, color: 'teal', gradient: 'from-[#00E5C0] to-[#00B4D8]' },
  file_audio: { label: 'Audio', accept: '.mp3,.wav,.m4a,.ogg,.webm,.aac', icon: Microphone, color: 'teal', gradient: 'from-[#00E5C0] to-[#00B4D8]' },
  // Video
  video: { label: 'Video', accept: '.mp4,.mov,.webm,.avi,.mkv', icon: Video, color: 'teal', gradient: 'from-[#00B4D8] to-[#00E5C0]' },
  video_file: { label: 'Video', accept: '.mp4,.mov,.webm,.avi,.mkv', icon: Video, color: 'teal', gradient: 'from-[#00B4D8] to-[#00E5C0]' },
  file_video: { label: 'Video', accept: '.mp4,.mov,.webm,.avi,.mkv', icon: Video, color: 'teal', gradient: 'from-[#00B4D8] to-[#00E5C0]' },
  // URL
  url: { label: 'URL', icon: Globe, color: 'cyan', gradient: 'from-cyan-500 to-blue-500' },
  // JSON
  json: { label: 'JSON', icon: FileText, color: 'yellow', gradient: 'from-yellow-500 to-amber-500' },
  // Automation types
  webhook: { label: 'Webhook', icon: WifiHigh, color: 'orange', gradient: 'from-orange-500 to-amber-500' },
  api_trigger: { label: 'API Trigger', icon: WifiHigh, color: 'amber', gradient: 'from-amber-500 to-yellow-500' },
  schedule: { label: 'Schedule', icon: Clock, color: 'blue', gradient: 'from-blue-500 to-indigo-500' },
  // Business data types
  services: { label: 'Services', icon: GearSix, color: 'green', gradient: 'from-green-500 to-emerald-500' },
  materials: { label: 'Materials', icon: FileXls, color: 'amber', gradient: 'from-amber-500 to-orange-500' },
  customer_details: { label: 'Customer Details', icon: FileText, color: 'teal', gradient: 'from-[#00E5C0] to-[#00B4D8]' }
};

// Audio input types (includes video since both have audio tracks)
const AUDIO_INPUT_TYPES = ['audio', 'audio_file', 'file_audio', 'video', 'video_file', 'file_video'];

// Document input types (includes all file types that need the file upload component)
const DOC_INPUT_TYPES = [
  'pdf', 'docx', 'xlsx', 'csv', 'file',
  'file_pdf', 'file_docx', 'file_xlsx', 'file_csv',
  'file_excel', 'excel', 'excel_file', 'spreadsheet',
  'image', 'file_image'
];

// Automation input types (webhook triggers, schedules)
const AUTOMATION_INPUT_TYPES = ['webhook', 'api_trigger', 'schedule', 'receive_webhook'];

// Business data input types (services, materials for quotes)
const BUSINESS_INPUT_TYPES = ['services', 'materials', 'customer_details'];

/**
 * Modern animated loading spinner
 */
const ModernSpinner = ({ size = 24, className = '' }) => (
  <div className={`relative ${className}`} style={{ width: size, height: size }}>
    <div className="absolute inset-0 rounded-full border-2 border-white/10" />
    <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#00E5C0] animate-spin" />
  </div>
);

/**
 * Animated progress bar for uploads
 */
const ProgressBar = ({ progress, className = '' }) => (
  <div className={`h-1 bg-white/10 rounded-full overflow-hidden ${className}`}>
    <div 
      className="h-full bg-gradient-to-r from-[#00E5C0] to-[#00B4D8] transition-all duration-300 ease-out"
      style={{ width: `${progress}%` }}
    />
  </div>
);

/**
 * Input type badge with icon
 */
const InputTypeBadge = ({ type, small = false }) => {
  const normalizedType = normalizeInputType(type);
  const config = INPUT_CONFIG[normalizedType] || { label: type, icon: File, gradient: 'from-gray-500 to-slate-500' };
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gradient-to-r ${config.gradient} bg-opacity-10 border border-white/10 ${small ? 'text-xs' : 'text-sm'}`}>
      <Icon size={small ? 12 : 14} className="text-white/80" />
      <span className="text-white/90 font-medium">{config.label}</span>
    </span>
  );
};

const RunAgentModal = ({ agent: agentProp, isOpen, onClose, onSuccess, workerId, outcomeCategory }) => {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const fileInputRef = useRef(null);
  const audioInputRef = useRef(null);
  
  // State
  const [agent, setAgent] = useState(agentProp);
  const [loadingAgent, setLoadingAgent] = useState(false);
  const [userText, setUserText] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedAudio, setUploadedAudio] = useState(null);
  const [refText, setRefText] = useState('');
  const [refFile, setRefFile] = useState(null);
  
  // Business data state (for quote-type agents)
  const [availableServices, setAvailableServices] = useState([]);
  const [availableMaterials, setAvailableMaterials] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [customerDetails, setCustomerDetails] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    address: ''
  });
  const [businessSettings, setBusinessSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [successState, setSuccessState] = useState(null);
  const [missingIntegrations, setMissingIntegrations] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [orgLLMConfigured, setOrgLLMConfigured] = useState(null);
  const [orgMismatch, setOrgMismatch] = useState(null);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  // Execution phase tracking for step-by-step UI
  const [executionPhase, setExecutionPhase] = useState(null); // 'uploading' | 'creating' | 'running' | null
  const [sessionOutput, setSessionOutput] = useState(null); // Fetched output data
  // Tab & history state
  const [activeTab, setActiveTab] = useState('run'); // 'run' | 'history'
  const [recentRuns, setRecentRuns] = useState([]);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [expandedRunId, setExpandedRunId] = useState(null);

  // Check if completion modal should be shown for this agent
  const getCompletionModalPref = (agentId) => {
    try {
      const prefs = JSON.parse(localStorage.getItem('agentCompletionModalPrefs') || '{}');
      return prefs[agentId] !== false; // default to true (show modal)
    } catch {
      return true;
    }
  };

  // Save completion modal preference for an agent
  const saveCompletionModalPref = (agentId, showModal) => {
    try {
      const prefs = JSON.parse(localStorage.getItem('agentCompletionModalPrefs') || '{}');
      prefs[agentId] = showModal;
      localStorage.setItem('agentCompletionModalPrefs', JSON.stringify(prefs));
    } catch (e) {
      console.error('Failed to save completion modal preference:', e);
    }
  };

  // Check if org LLM is configured (required for agent execution)
  useEffect(() => {
    const checkOrgLLM = async () => {
      if (!isOpen || !activeOrganization?.organization_id) return;
      
      try {
        const response = await fetchWithOrg(
          `${BACKEND_URL}/api/org/llm-config`,
          { credentials: 'include' },
          activeOrganization.organization_id
        );
        if (response.ok) {
          const data = await response.json();
          setOrgLLMConfigured(data.has_key === true);
        } else {
          setOrgLLMConfigured(false);
        }
      } catch (err) {
        console.error('Error checking org LLM config:', err);
        setOrgLLMConfigured(false);
      }
    };
    
    checkOrgLLM();
  }, [isOpen, activeOrganization?.organization_id]);

  // P0 FIX: Check for organization mismatch when modal opens
  // This prevents "Agent not found" errors when user switches orgs while browsing agents
  useEffect(() => {
    if (!isOpen) {
      setOrgMismatch(null);
      return;
    }
    
    const agentOrgId = agentProp?.org_id || agentProp?.organization_id;
    const currentOrgId = activeOrganization?.organization_id;
    
    if (agentOrgId && currentOrgId && agentOrgId !== currentOrgId) {
      console.warn('[RunAgentModal] Organization mismatch detected:', {
        agentOrgId,
        currentOrgId,
        agentName: agentProp?.name
      });
      setOrgMismatch({
        agentOrgId,
        currentOrgId,
        agentName: agentProp?.name
      });
    } else {
      setOrgMismatch(null);
    }
  }, [isOpen, agentProp?.org_id, agentProp?.organization_id, activeOrganization?.organization_id, agentProp?.name]);

  // Fetch full agent data when modal opens (to get enriched template data)
  useEffect(() => {
    const fetchFullAgentData = async () => {
      // Support both 'id' and 'agent_id' (ACC API uses agent_id)
      const agentId = agentProp?.id || agentProp?.agent_id;
      if (!isOpen || !agentId || !activeOrganization) return;
      
      // P0 FIX: Don't fetch if org mismatch detected - prevents "Agent not found" error
      const agentOrgId = agentProp?.org_id || agentProp?.organization_id;
      if (agentOrgId && agentOrgId !== activeOrganization.organization_id) {
        console.warn('[RunAgentModal] Skipping fetch due to org mismatch');
        setOrgMismatch({
          agentOrgId,
          currentOrgId: activeOrganization.organization_id,
          agentName: agentProp?.name
        });
        return;
      }
      
      // P0 FIX: ALWAYS fetch full agent data if accepted_inputs is missing
      // This is required for input-aware modal rendering (file uploads, audio, etc.)
      // The list API doesn't include accepted_inputs, so we must fetch the full agent
      const hasAcceptedInputs = Array.isArray(agentProp.accepted_inputs) && agentProp.accepted_inputs.length > 0;
      
      if (hasAcceptedInputs && agentProp.agent_config) {
        // Agent already has all the data we need
        console.log('[RunAgentModal] Using existing agent data:', {
          id: agentProp.id,
          accepted_inputs: agentProp.accepted_inputs
        });
        setAgent(agentProp);
        return;
      }
      
      setLoadingAgent(true);
      try {
        const response = await fetchWithOrg(
          `${BACKEND_URL}/api/agents/${agentId}`,
          {},
          activeOrganization.organization_id
        );
        
        if (response.ok) {
          const fullAgent = await response.json();
          console.log('[RunAgentModal] Fetched full agent data:', {
            id: fullAgent.id,
            name: fullAgent.name,
            accepted_inputs: fullAgent.accepted_inputs,
            required_inputs: fullAgent.required_inputs,
            fixed_intent: fullAgent.fixed_intent,
            is_template_instance: fullAgent.is_template_instance,
            source_template_id: fullAgent.source_template_id
          });
          setAgent(fullAgent);
        } else if (response.status === 404) {
          // P0 FIX: 404 likely means agent belongs to different org
          // Show org mismatch error instead of cryptic "Agent not found"
          console.warn('[RunAgentModal] Agent not found (404) - likely org mismatch');
          const errorData = await response.json().catch(() => ({}));
          if (errorData.detail?.includes('organization')) {
            setOrgMismatch({
              agentOrgId: 'unknown',
              currentOrgId: activeOrganization.organization_id,
              agentName: agentProp?.name
            });
          } else {
            setError('Agent not found. It may have been deleted or you may not have access.');
          }
        } else {
          console.error('[RunAgentModal] Failed to fetch agent data');
          setAgent(agentProp);
        }
      } catch (err) {
        console.error('[RunAgentModal] Error fetching agent:', err);
        setAgent(agentProp);
      } finally {
        setLoadingAgent(false);
      }
    };
    
    fetchFullAgentData();
  }, [isOpen, agentProp?.id, agentProp?.agent_id, activeOrganization]);

  // Fetch training data and business settings when business inputs are shown
  useEffect(() => {
    const fetchBusinessData = async () => {
      const agentId = agent?.id || agent?.agent_id;
      if (!isOpen || !agentId || !activeOrganization?.organization_id) return;
      
      // Check if agent accepts business inputs
      const acceptedInputs = agent?.accepted_inputs || [];
      const needsBusinessData = acceptedInputs.some(i => BUSINESS_INPUT_TYPES.includes(i));
      
      if (!needsBusinessData) return;
      
      try {
        // Fetch training data (services, materials)
        const trainingResponse = await fetchWithOrg(
          `${BACKEND_URL}/api/agents/${agentId}/training-data`,
          { credentials: 'include' },
          activeOrganization.organization_id
        );
        
        if (trainingResponse.ok) {
          const data = await trainingResponse.json();
          setAvailableServices(data.training_data?.services || []);
          setAvailableMaterials(data.training_data?.materials || []);
        }
        
        // Fetch business settings (currency, tax)
        const settingsResponse = await fetchWithOrg(
          `${BACKEND_URL}/api/org/business-settings`,
          { credentials: 'include' },
          activeOrganization.organization_id
        );
        
        if (settingsResponse.ok) {
          const data = await settingsResponse.json();
          setBusinessSettings(data.settings);
        }
      } catch (err) {
        console.error('Error fetching business data:', err);
      }
    };
    
    fetchBusinessData();
  }, [isOpen, agent?.id, agent?.agent_id, agent?.accepted_inputs, activeOrganization?.organization_id]);

  // Schema-driven configuration - ONLY based on agent schema
  const schema = useMemo(() => {
    // Get accepted_inputs (the source of truth) and normalize to handle both formats
    const rawAcceptedInputs = Array.isArray(agent?.accepted_inputs) && agent.accepted_inputs.length > 0
      ? agent.accepted_inputs
      : ['text'];
    
    // Normalize input types to handle both legacy and new builder formats
    const acceptedInputs = normalizeInputTypes(rawAcceptedInputs);
    
    // Get required_inputs and normalize
    const rawRequiredInputs = Array.isArray(agent?.required_inputs) 
      ? agent.required_inputs 
      : [];
    const requiredInputs = normalizeInputTypes(rawRequiredInputs);
    
    // Determine mode: FIXED (template) vs OPEN (custom)
    const isFixedMode = !!(
      agent?.is_template_instance || 
      agent?.source_template_id || 
      agent?.fixed_intent === true
    );
    
    // Determine which inputs to show based ONLY on normalized accepted_inputs
    const showAudioUpload = acceptedInputs.some(i => AUDIO_INPUT_TYPES.includes(i));
    const showFileUpload = acceptedInputs.some(i => DOC_INPUT_TYPES.includes(i)) || acceptedInputs.includes('file');
    const showUrlInput = acceptedInputs.includes('url');
    const showAutomation = acceptedInputs.some(i => AUTOMATION_INPUT_TYPES.includes(i));
    const showBusinessInputs = acceptedInputs.some(i => BUSINESS_INPUT_TYPES.includes(i));
    const showWebhookConfig = acceptedInputs.includes('webhook') || acceptedInputs.includes('api_trigger');
    const showImageUpload = acceptedInputs.includes('image');
    
    // P0 FIX: Always show text input for additional notes/context
    // For fixed-mode agents with file/audio uploads, the text field is optional (for additional instructions)
    // For open-mode agents without other inputs, text is the primary input
    const showTextInput = true; // Always show - it's either primary input or "additional notes"
    const showServicesSelector = acceptedInputs.includes('services');
    const showMaterialsSelector = acceptedInputs.includes('materials');
    const showCustomerForm = acceptedInputs.includes('customer_details');
    
    // Check if video is accepted (for label purposes)
    const acceptsVideo = acceptedInputs.some(i => ['video', 'video_file', 'file_video'].includes(i));
    const acceptsAudio = acceptedInputs.some(i => AUDIO_INPUT_TYPES.includes(i));
    const acceptsDocuments = acceptedInputs.some(i => DOC_INPUT_TYPES.includes(i)) || acceptedInputs.includes('file');
    
    // Determine required inputs (using normalized types)
    const audioRequired = requiredInputs.some(i => AUDIO_INPUT_TYPES.includes(i));
    const fileRequired = requiredInputs.some(i => DOC_INPUT_TYPES.includes(i)) || requiredInputs.includes('file');
    const urlRequired = requiredInputs.includes('url');
    const imageRequired = requiredInputs.includes('image');
    const textRequired = !isFixedMode && showTextInput && !showAudioUpload && !showFileUpload;
    
    // Build accepted file extensions for document upload
    // IMPORTANT: When ANY document type is accepted, allow ALL common document formats
    // Users should be able to upload PDFs, Word docs, Excel files, etc. regardless of the
    // specific type mentioned in the opportunity/agent config. We detect internally.
    const hasDocumentInput = acceptedInputs.some(i => DOC_INPUT_TYPES.includes(i)) || acceptedInputs.includes('file');
    
    // Universal document types - accept all common formats when any document input is specified
    const universalDocAccept = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.odt,.ods,.ppt,.pptx,.odp,.png,.jpg,.jpeg,.webp,.heic,.heif,.gif,.bmp';
    
    const acceptedFileExtensions = hasDocumentInput ? universalDocAccept : '';
    
    // Get accepted input types for display (show badges)
    const acceptedInputTypes = acceptedInputs.filter(i => i !== 'text');
    
    // Get output schema for preview
    const outputSchema = agent?.output_schema;
    
    // Get expected outputs from flow nodes if available
    const expectedOutputs = agent?.expected_outputs || [];
    
    // Determine CTA text based on mode and agent name
    let ctaText = 'Run Agent';
    let ctaIcon = Play;
    if (isFixedMode) {
      const name = (agent?.name || '').toLowerCase();
      if (name.includes('analyst') || name.includes('quality') || name.includes('analyze')) {
        ctaText = 'Analyze';
        ctaIcon = Lightning;
      } else if (name.includes('generator') || name.includes('writer') || name.includes('create')) {
        ctaText = 'Generate';
        ctaIcon = Sparkle;
      } else if (name.includes('summarizer') || name.includes('summary') || name.includes('notes')) {
        ctaText = 'Summarize';
        ctaIcon = FileText;
      } else if (name.includes('extractor') || name.includes('extract')) {
        ctaText = 'Extract';
        ctaIcon = DownloadSimple;
      } else if (name.includes('monitor') || name.includes('watch')) {
        ctaText = 'Start Monitoring';
        ctaIcon = Lightning;
      } else {
        ctaText = 'Run Agent';
        ctaIcon = Play;
      }
    }
    
    // Reference material config from agent_config
    const agentConfig = agent?.agent_config || {};
    const refInputType = agentConfig.ref_input_type || 'text';
    const refRequired = agentConfig.ref_required || false;
    const showRefText = (refInputType === 'text' || refInputType === 'text_and_file') && (showFileUpload || showImageUpload);
    const showRefFile = (refInputType === 'file' || refInputType === 'text_and_file') && (showFileUpload || showImageUpload);

    return {
      acceptedInputs,
      requiredInputs,
      isFixedMode,
      showAudioUpload,
      showFileUpload,
      showUrlInput,
      showTextInput,
      showAutomation,
      showBusinessInputs,
      showWebhookConfig,
      showServicesSelector,
      showMaterialsSelector,
      showCustomerForm,
      audioRequired,
      fileRequired,
      urlRequired,
      textRequired,
      acceptsVideo,
      acceptsAudio,
      acceptsDocuments,
      acceptedFileExtensions,
      acceptedInputTypes,
      outputSchema,
      expectedOutputs,
      ctaText,
      ctaIcon,
      showImageUpload,
      imageRequired,
      showRefText,
      showRefFile,
      refRequired,
    };
  }, [agent]);

  // DEBUG: Log modal variant resolution for IAO validation
  useEffect(() => {
    if (isOpen && agent && schema) {
      const modalVariant = schema.isVoice ? 'voice_s2s_informational' :
        schema.isFixedMode ? 
          (schema.acceptsAudio || schema.acceptsVideo ? 'fixed_intent_audio_video' :
           schema.acceptsDocuments ? 'fixed_intent_file_upload' : 'fixed_intent_text_only') :
        'flexible_prompt';
      
      console.log('🔍 IAO Modal Debug:', {
        agent_id: agent.id,
        agent_name: agent.name,
        capability_profile: agent.is_voice_template ? 'live_voice_s2s_agent' : 'text_agent',
        fixed_intent: schema.isFixedMode,
        accepted_inputs: agent.accepted_inputs || ['text'],
        required_inputs: agent.required_inputs || [],
        resolved_modal_variant_id: modalVariant,
        upload_types_enabled: schema.acceptedInputTypes,
        cta_label: schema.ctaText
      });
    }
  }, [isOpen, agent, schema]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setUserText('');
      setUrlInput('');
      setUploadedFile(null);
      setUploadedAudio(null);
      setRefText('');
      setRefFile(null);
      setError(null);
      setSuccessState(null);
      setMissingIntegrations(null);
      setActiveTab('run');
      setExpandedRunId(null);
    }
  }, [isOpen]);

  // Fetch recent runs when switching to history tab or when modal opens
  const fetchRecentRuns = async () => {
    const agentId = agent?.id || agent?.agent_id;
    if (!agentId || !activeOrganization?.organization_id) return;
    setLoadingRuns(true);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/my/sessions?agent_id=${agentId}&limit=15`,
        {},
        activeOrganization.organization_id
      );
      if (res.ok) {
        const data = await res.json();
        setRecentRuns(data.sessions || []);
      }
    } catch (e) {
      console.warn('[RunAgentModal] Failed to fetch recent runs:', e);
    } finally {
      setLoadingRuns(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'history' && isOpen) {
      fetchRecentRuns();
    }
  }, [activeTab, isOpen, agent?.id, agent?.agent_id, activeOrganization?.organization_id]); // eslint-disable-line

  // Validation: Check if CTA should be enabled
  const canSubmit = useMemo(() => {
    // Check required audio
    if (schema.audioRequired && !uploadedAudio) return false;
    
    // Check required file
    if (schema.fileRequired && !uploadedFile) return false;
    
    // Check required URL
    if (schema.urlRequired && !urlInput.trim()) return false;
    
    // Check required text (only for open mode with no file/audio)
    if (schema.textRequired && !userText.trim()) return false;
    
    // For open mode without specific requirements, need at least some input
    if (!schema.isFixedMode && !userText.trim() && !uploadedFile && !uploadedAudio && !urlInput.trim()) {
      return false;
    }
    
    return true;
  }, [schema, uploadedAudio, uploadedFile, urlInput, userText]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        setError('File size must be less than 50MB');
        return;
      }
      setUploadedFile(file);
      setError(null);
    }
  };

  // Handle audio selection
  const handleAudioSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 100 * 1024 * 1024) {
        setError('Audio file size must be less than 100MB');
        return;
      }
      setUploadedAudio(file);
      setError(null);
    }
  };

  // UploadSimple file to server
  const uploadFileToServer = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    formData.append('agent_id', agent.id);
    
    // Note: upload-input is on /api/agent (singular), not /api/agents (plural)
    const response = await fetchWithOrg(
      `${BACKEND_URL}/api/agent/${agent.id}/upload-input`,
      { method: 'POST', body: formData },
      activeOrganization.organization_id
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to upload file');
    }
    
    return await response.json();
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!canSubmit) {
      setError('Please provide all required inputs');
      return;
    }

    const agentOrgId = agent?.org_id || agent?.organization_id;
    const currentOrgId = activeOrganization?.organization_id;
    if (agentOrgId && currentOrgId && agentOrgId !== currentOrgId) {
      setOrgMismatch({ agentOrgId, currentOrgId, agentName: agent?.name });
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setExecutionPhase('uploading');

      const inputPayload = { text: userText };
      
      // Upload audio if present
      if (uploadedAudio) {
        setUploading(true);
        try {
          const audioResult = await uploadFileToServer(uploadedAudio, 'audio');
          inputPayload.audio_path = audioResult.file_path;
          inputPayload.audio_name = uploadedAudio.name;
          inputPayload.audio_type = uploadedAudio.type;
        } catch (err) {
          throw new Error(`Audio upload failed: ${err.message}`);
        } finally {
          setUploading(false);
        }
      }
      
      // Upload file if present
      if (uploadedFile) {
        setUploading(true);
        try {
          const fileResult = await uploadFileToServer(uploadedFile, 'document');
          inputPayload.file_path = fileResult.file_path;
          inputPayload.file_name = uploadedFile.name;
          inputPayload.file_type = uploadedFile.type;
        } catch (err) {
          throw new Error(`File upload failed: ${err.message}`);
        } finally {
          setUploading(false);
        }
      }
      
      // Add URL if present
      if (urlInput.trim()) {
        inputPayload.url = urlInput;
      }

      // Add runtime reference material
      if (refText.trim()) {
        inputPayload.reference_text = refText.trim();
      }
      if (refFile) {
        try {
          const refResult = await uploadFileToServer(refFile, 'reference');
          inputPayload.reference_file_path = refResult.file_path;
          inputPayload.reference_file_name = refFile.name;
        } catch (err) {
          throw new Error(`Reference file upload failed: ${err.message}`);
        }
      }

      // Determine input type
      let inputType = 'text';
      if (uploadedAudio) inputType = 'audio';
      else if (uploadedFile) inputType = 'file';

      setExecutionPhase('creating');

      const sessionPayload = {
        agent_id: agent.id,
        input_type: inputType,
        input_payload: inputPayload,
        mode: schema.isFixedMode ? 'fixed' : 'open',
        source: 'manual'
      };

      // Create session
      const createResponse = await fetchWithOrg(
        `${BACKEND_URL}/api/agents/${agent.id}/sessions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(sessionPayload)
        },
        activeOrganization.organization_id
      );

      if (!createResponse.ok) {
        const errorData = await createResponse.json();
        if (createResponse.status === 403) {
          setError(errorData.detail || 'Seat required. Please contact your administrator.');
          return;
        }
        throw new Error(errorData.detail || 'Failed to create session');
      }

      const sessionData = await createResponse.json();
      const sessionId = sessionData.session_id;

      setExecutionPhase('running');

      // Run session
      const runResponse = await fetchWithOrg(
        `${BACKEND_URL}/api/sessions/${sessionId}/run`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        },
        activeOrganization.organization_id
      );

      if (runResponse.ok) {
        const runData = await runResponse.json();
        const isSuccess = runData.status === 'completed' && runData.success;
        
        // Fetch the session output for inline display
        let outputData = null;
        try {
          const outputResponse = await fetchWithOrg(
            `${BACKEND_URL}/api/sessions/${sessionId}`,
            {},
            activeOrganization.organization_id
          );
          if (outputResponse.ok) {
            outputData = await outputResponse.json();
          }
        } catch (e) {
          console.warn('[RunAgentModal] Could not fetch session output:', e);
        }

        setSessionOutput(outputData);
        setSuccessState({
          sessionId,
          message: isSuccess ? 'Execution Complete' : 'Completed with Issues',
          isSuccess,
          output: runData.output || outputData?.output || outputData?.session?.output || null,
          duration: runData.duration_ms || null
        });
        
        // Refresh recent runs so new run appears in history
        fetchRecentRuns();
        onSuccess?.(sessionId);
      } else {
        const errorData = await runResponse.json().catch(() => ({}));
        
        if (runResponse.status === 403 && errorData.detail?.error === 'missing_integrations') {
          setMissingIntegrations(errorData.detail);
          return;
        }
        
        throw new Error(errorData.detail?.message || errorData.detail || 'Failed to run');
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'Failed to run. Please try again.');
    } finally {
      setLoading(false);
      setUploading(false);
      setExecutionPhase(null);
    }
  };

  const handleViewResults = () => {
    if (workerId) {
      // Navigate to worker Activity section instead of legacy sessions
      navigate(`/workers/${workerId}/detail`);
      onClose();
    } else if (successState?.sessionId) {
      navigate(`/workers`);
      onClose();
    }
  };

  const handleCloseAndReset = () => {
    setSuccessState(null);
    setMissingIntegrations(null);
    setOrgMismatch(null);
    setSessionOutput(null);
    setExecutionPhase(null);
    setUserText('');
    setUrlInput('');
    setUploadedFile(null);
    setUploadedAudio(null);
    setRefText('');
    setRefFile(null);
    setError(null);
    onClose();
  };

  // Rerun: go back to input state
  const handleRerun = () => {
    setSuccessState(null);
    setSessionOutput(null);
    setExecutionPhase(null);
    setError(null);
  };

  // Prefill form from a past run's inputs
  const handleUseInputs = (run) => {
    const payload = run.input_payload || {};
    if (payload.text) setUserText(payload.text);
    if (payload.url) setUrlInput(payload.url);
    setActiveTab('run');
  };

  // Navigate to a run's full details (Activity on worker detail)
  const handleViewRunDetails = (sessionId) => {
    if (workerId) {
      navigate(`/workers/${workerId}/detail`);
    } else {
      navigate(`/workers`);
    }
    onClose();
  };

  // Time-ago formatter
  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const now = new Date();
    const d = new Date(dateStr);
    const secs = Math.floor((now - d) / 1000);
    if (secs < 60) return 'just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  };

  // Summarise text to N chars
  const summarise = (val, max = 80) => {
    if (!val) return null;
    const s = typeof val === 'string' ? val : (val.text || val.summary || val.result || JSON.stringify(val));
    return s.length > max ? s.slice(0, max) + '...' : s;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!isOpen) return null;

  // P0 FIX: Organization mismatch view - prevents "Agent not found" error
  if (orgMismatch) {
    return (
      <>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={onClose} />
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md z-50 px-4">
          <div className="glass rounded-2xl border border-[rgba(255,255,255,0.2)] shadow-2xl p-6">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-500/20 flex items-center justify-center">
              <WarningCircle size={28} className="text-amber-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2 text-center">Organization Mismatch</h2>
            <p className="text-sm text-gray-400 mb-4 text-center">
              This agent belongs to a different organization. Please switch to the correct organization to run this agent.
            </p>
            <div className="p-3 bg-[rgba(255,255,255,0.05)] rounded-lg mb-4">
              <p className="text-xs text-gray-500 mb-1">Agent</p>
              <p className="text-sm text-white font-medium">{orgMismatch.agentName || 'Unknown Agent'}</p>
            </div>
            <div className="flex gap-3">
              <button 
                onClick={onClose} 
                className="flex-1 px-4 py-2.5 bg-[#00E5C0] hover:bg-[#00CCB0] text-[#0D0F11] rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Loading agent data state
  if (loadingAgent) {
    return (
      <>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md z-50 px-4">
          <div className="glass rounded-2xl border border-[rgba(255,255,255,0.2)] shadow-2xl p-6">
            <div className="flex items-center justify-center gap-3">
              <CircleNotch size={24} className="animate-spin text-[#00E5C0]" />
              <span className="text-white">Loading...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Missing integrations view
  if (missingIntegrations) {
    return (
      <>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={handleCloseAndReset} />
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md z-50 px-4">
          <div className="glass rounded-2xl border border-[rgba(255,255,255,0.2)] shadow-2xl p-6">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-500/20 flex items-center justify-center">
              <WarningCircle size={28} className="text-amber-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2 text-center">Missing Integrations</h2>
            <p className="text-sm text-gray-400 mb-4 text-center">Required integrations:</p>
            <div className="flex flex-wrap gap-2 justify-center mb-4">
              {(missingIntegrations.missing || []).map((i, idx) => (
                <span key={idx} className="px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm">
                  {i.charAt(0).toUpperCase() + i.slice(1)}
                </span>
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={handleCloseAndReset} className="flex-1 px-4 py-2 glass text-white rounded-lg hover:bg-[rgba(255,255,255,0.1)]">
                Cancel
              </button>
                <Link to="/partner-pro/settings/integrations" onClick={handleCloseAndReset}
                className="flex-1 px-4 py-2 bg-[#00E5C0] hover:bg-[#00CCB0] text-[#0D0F11] rounded-lg flex items-center justify-center gap-2 transition-colors">
                <GearSix size={16} /> Connect
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Handle close with "don't show again" preference
  const handleCloseWithPreference = () => {
    if (dontShowAgain) {
      const agentId = agent?.id || agent?.agent_id;
      saveCompletionModalPref(agentId, false);
    }
    handleCloseAndReset();
  };

  // Helper to extract displayable output text from session data
  const getOutputText = () => {
    const o = successState?.output;
    if (!o) return null;
    if (typeof o === 'string') return o;
    if (o.text) return o.text;
    if (o.summary) return o.summary;
    if (o.result) return typeof o.result === 'string' ? o.result : JSON.stringify(o.result, null, 2);
    if (o.content) return typeof o.content === 'string' ? o.content : JSON.stringify(o.content, null, 2);
    // Fallback: try to get from session output
    const so = sessionOutput?.output || sessionOutput?.session?.output;
    if (so) {
      if (typeof so === 'string') return so;
      if (so.text) return so.text;
      if (so.summary) return so.summary;
      if (so.result) return typeof so.result === 'string' ? so.result : JSON.stringify(so.result, null, 2);
    }
    // Last resort: show the entire object
    return JSON.stringify(o, null, 2);
  };

  // ── EXECUTION PHASE VIEW ──
  // Shows step-by-step progress while the agent is running
  if (loading && executionPhase) {
    const phases = [
      { key: 'uploading', label: 'Preparing input', icon: UploadSimple },
      { key: 'creating', label: 'Creating session', icon: Lightning },
      { key: 'running', label: 'Running agent', icon: Play }
    ];
    const currentIdx = phases.findIndex(p => p.key === executionPhase);

    return (
      <>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md z-50 px-4">
          <div className="bg-[#0f1117] rounded-2xl border border-white/10 shadow-2xl p-8" data-testid="execution-phase-view">
            {/* Pulsing icon */}
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[rgba(0,229,192,0.12)] flex items-center justify-center">
              <CircleNotch size={28} className="text-[#00E5C0] animate-spin" />
            </div>
            <h2 className="text-lg font-semibold text-white mb-1 text-center">{agent?.name || 'Agent'}</h2>
            <p className="text-sm text-gray-500 mb-8 text-center">Executing your request...</p>

            {/* Step indicators */}
            <div className="space-y-3">
              {phases.map((phase, idx) => {
                const PhaseIcon = phase.icon;
                const isActive = idx === currentIdx;
                const isDone = idx < currentIdx;
                return (
                  <div
                    key={phase.key}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                      isActive ? 'bg-[rgba(0,229,192,0.08)] border border-[rgba(0,229,192,0.25)]' :
                      isDone ? 'bg-white/[0.03] border border-white/5' :
                      'bg-white/[0.02] border border-transparent'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isActive ? 'bg-[rgba(0,229,192,0.15)]' : isDone ? 'bg-emerald-500/15' : 'bg-white/5'
                    }`}>
                      {isDone ? (
                        <CheckCircle size={16} className="text-emerald-400" weight="fill" />
                      ) : isActive ? (
                        <CircleNotch size={16} className="text-[#00E5C0] animate-spin" />
                      ) : (
                        <PhaseIcon size={16} className="text-gray-600" />
                      )}
                    </div>
                    <span className={`text-sm font-medium ${
                      isActive ? 'text-white' : isDone ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {phase.label}
                    </span>
                    {isActive && (
                      <div className="ml-auto flex gap-0.5">
                        <span className="w-1.5 h-1.5 bg-[#00E5C0] rounded-full animate-pulse" />
                        <span className="w-1.5 h-1.5 bg-[#00E5C0] rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                        <span className="w-1.5 h-1.5 bg-[#00E5C0] rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── RESULT VIEW ──
  // Shows output after execution completes
  if (successState) {
    const outputText = getOutputText();
    const hasOutput = outputText && outputText.trim().length > 0;

    return (
      <>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={handleCloseWithPreference} />
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg z-50 px-4">
          <div className="bg-[#0f1117] rounded-2xl border border-white/10 shadow-2xl overflow-hidden" data-testid="result-view">
            {/* Status header */}
            <div className={`px-6 pt-6 pb-4 ${successState.isSuccess ? 'border-b border-emerald-500/10' : 'border-b border-amber-500/10'}`}>
              <div className="flex items-start gap-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  successState.isSuccess ? 'bg-emerald-500/15' : 'bg-amber-500/15'
                }`}>
                  {successState.isSuccess ? (
                    <CheckCircle size={22} className="text-emerald-400" weight="fill" />
                  ) : (
                    <WarningCircle size={22} className="text-amber-400" weight="fill" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base font-semibold text-white">{successState.message}</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {agent?.name || 'Agent'}{successState.duration ? ` · ${(successState.duration / 1000).toFixed(1)}s` : ''}
                  </p>
                </div>
                <button onClick={handleCloseWithPreference} className="p-1.5 text-gray-500 hover:text-white rounded-lg hover:bg-white/5 transition-colors">
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Output body */}
            <div className="px-6 py-4 max-h-[50vh] overflow-y-auto">
              {hasOutput ? (
                <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4">
                  <pre className="text-sm text-gray-300 whitespace-pre-wrap break-words font-sans leading-relaxed" data-testid="execution-output">
                    {outputText}
                  </pre>
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-sm text-gray-500">
                    {successState.isSuccess
                      ? 'Execution completed. View full results for detailed output.'
                      : 'The agent completed with some issues. Check the session for details.'}
                  </p>
                </div>
              )}

              {/* AI Output Notice */}
              {successState.isSuccess && hasOutput && (
                <AIOutputNotice variant="compact" className="justify-center mt-3" />
              )}
            </div>

            {/* Action footer */}
            <div className="px-6 py-4 border-t border-white/5 flex items-center gap-3">
              <button
                onClick={handleRerun}
                data-testid="rerun-agent-btn"
                className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm rounded-xl transition-colors"
              >
                <ArrowClockwise size={16} />
                Run Again
              </button>
              <div className="flex-1" />
              {successState.isSuccess && successState.sessionId && (
                <button
                  onClick={() => setShowExportModal(true)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm rounded-xl transition-colors"
                >
                  <DownloadSimple size={16} /> Export
                </button>
              )}
              <button
                onClick={handleViewResults}
                data-testid="view-results-btn"
                className="flex items-center gap-2 px-5 py-2.5 bg-[#00E5C0] hover:bg-[#00CCB0] text-[#0D0F11] text-sm font-medium rounded-xl transition-colors"
              >
                <ArrowSquareOut size={16} /> View Activity
              </button>
            </div>
          </div>
        </div>
        
        {/* Export Modal */}
        {showExportModal && successState.sessionId && (
          <UniversalExportModal
            isOpen={showExportModal}
            onClose={() => setShowExportModal(false)}
            agentId={agent?.id || agent?.agent_id}
            agentName={agent?.name}
            sessionId={successState.sessionId}
            contextTitle={userText?.substring(0, 50)}
            sourceFilename={uploadedFile?.name || uploadedAudio?.name}
          />
        )}
      </>
    );
  }

  // VOICE INPUT AGENT (Meeting Agent) — Informational Only View
  // These agents run AUTOMATICALLY when meetings occur - no manual input needed
  const isVoiceInputAgent = agent?.capability_profile === 'voice_input_agent';
  
  if (isVoiceInputAgent) {
    // Get connected platforms from meeting_config
    const connectedPlatforms = agent?.meeting_config?.connected_platforms || {};
    const platformCount = Object.keys(connectedPlatforms).length;
    const isConfigured = agent?.wizard_completed && platformCount > 0;
    const isActive = agent?.status === 'active' || agent?.billing_status === 'active';
    
    // Platform display names
    const platformNames = {
      microsoft_teams: { name: 'Microsoft Teams', icon: '🟦' },
      google_meet: { name: 'Google Meet', icon: '🟢' },
      zoom: { name: 'Zoom', icon: '🔵' }
    };

    return (
      <>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={onClose} />
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg z-50 px-4">
          <div className="glass rounded-2xl border border-[rgba(255,255,255,0.2)] shadow-2xl">
            {/* Header */}
            <div className="relative px-6 py-5 border-b border-[rgba(255,255,255,0.1)]">
              <button onClick={onClose} className="absolute top-5 right-5 text-gray-400 hover:text-white">
                <X size={20} />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#00E5C0] to-[#00B4D8] flex items-center justify-center">
                  <Headphones size={28} className="text-[#0D0F11]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{agent?.name}</h2>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="px-2 py-0.5 text-xs rounded-full bg-[rgba(0,229,192,0.12)] text-[#00E5C0] flex items-center gap-1">
                      <WifiHigh size={12} />
                      Live Meeting Agent
                    </span>
                    {isActive && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400">
                        Active
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-5 space-y-5">
              {/* Informational ChatText */}
              <div className="p-4 rounded-xl bg-[rgba(0,229,192,0.06)] border border-[rgba(0,229,192,0.15)]">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[rgba(0,229,192,0.12)] flex items-center justify-center flex-shrink-0">
                    <Video size={20} className="text-[#00E5C0]" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Automatic Meeting Capture</h4>
                    <p className="text-sm text-gray-400">
                      This agent automatically joins and captures notes from your meetings. 
                      No manual input needed — just start a meeting on a connected platform.
                    </p>
                  </div>
                </div>
              </div>

              {/* Agent Description */}
              {agent?.description && (
                <div>
                  <h4 className="text-sm font-medium text-white mb-2">What This Agent Does</h4>
                  <p className="text-sm text-gray-400">{agent.description}</p>
                </div>
              )}

              {/* Connected Platforms */}
              <div>
                <h4 className="text-sm font-medium text-white mb-3">Connected Platforms</h4>
                {platformCount > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(connectedPlatforms).map(([platformId, platformData]) => {
                      const platform = platformNames[platformId] || { name: platformId, icon: '📺' };
                      return (
                        <div 
                          key={platformId}
                          className="flex items-center justify-between p-3 rounded-lg bg-green-500/10 border border-green-500/20"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-xl">{platform.icon}</span>
                            <span className="text-white font-medium">{platform.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle size={16} className="text-green-400" />
                            <span className="text-xs text-green-400">Connected</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <div className="flex items-center gap-2 text-amber-400 text-sm">
                      <WarningCircle size={16} />
                      <span>No platforms connected. Configure integrations to enable automatic meeting capture.</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Capture GearSix Preview */}
              {agent?.meeting_config?.capture_rules && (
                <div>
                  <h4 className="text-sm font-medium text-white mb-2">Capture Settings</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(agent.meeting_config.capture_rules)
                      .filter(([_, enabled]) => enabled)
                      .map(([rule]) => (
                        <span 
                          key={rule}
                          className="px-2 py-1 text-xs rounded-full bg-[rgba(255,255,255,0.05)] text-gray-300 capitalize"
                        >
                          {rule.replace(/_/g, ' ')}
                        </span>
                      ))
                    }
                  </div>
                </div>
              )}

              {/* Status Indicator */}
              <div className={`p-4 rounded-xl ${
                isActive && isConfigured 
                  ? 'bg-green-500/10 border border-green-500/20' 
                  : 'bg-amber-500/10 border border-amber-500/20'
              }`}>
                <div className="flex items-center gap-3">
                  {isActive && isConfigured ? (
                    <>
                      <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse" />
                      <span className="text-green-400 font-medium">Ready to capture meetings</span>
                    </>
                  ) : !isConfigured ? (
                    <>
                      <WarningCircle size={18} className="text-amber-400" />
                      <span className="text-amber-400">Setup required — connect at least one platform</span>
                    </>
                  ) : (
                    <>
                      <WarningCircle size={18} className="text-amber-400" />
                      <span className="text-amber-400">Agent not active — activate to start capturing</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-[rgba(255,255,255,0.1)] flex justify-between gap-3">
              <button
                onClick={onClose}
                className="px-5 py-2.5 glass text-white rounded-lg hover:bg-[rgba(255,255,255,0.1)]"
              >
                Close
              </button>
              <Link
                to={`/partner-pro/ai-workforce/agents/${agent?.id || agent?.agent_id}/meeting-wizard`}
                onClick={onClose}
                className="px-5 py-2.5 bg-[#00E5C0] hover:bg-[#00CCB0] text-[#0D0F11] rounded-lg transition-colors flex items-center gap-2"
              >
                <GearSix size={16} />
                Manage Integrations
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Block if org LLM not configured (HARD REQUIREMENT - NO FALLBACK)
  if (orgLLMConfigured === false) {
    return (
      <>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={onClose} />
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md z-50 px-4">
          <div className="glass rounded-2xl border border-[rgba(255,255,255,0.2)] shadow-2xl p-8">
            <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white">
              <X size={20} />
            </button>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
                <WarningCircle size={32} className="text-amber-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Agent Unavailable</h3>
              <p className="text-gray-400 mb-6">
                This agent cannot run because your organisation has not configured an AI provider.
              </p>
              <div className="flex gap-3 justify-center">
                <a
                  href="/partner-pro/organisation/ai-configuration"
                  className="px-6 py-2 bg-[#00E5C0] hover:bg-[#00CCB0] text-[#0D0F11] rounded-lg transition-colors inline-flex items-center gap-2"
                >
                  <GearSix size={16} />
                  Configure AI Provider
                </a>
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.1)] text-gray-300 rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Main modal
  return (
    <>
      {/* Backdrop with blur effect */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 transition-opacity duration-300" 
        onClick={onClose} 
      />
      
      {/* Modal container with animation */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl z-50 px-4 max-h-[90vh] overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
        <div className="relative bg-gradient-to-b from-slate-900/95 to-slate-950/95 rounded-3xl border border-white/10 shadow-2xl shadow-black/50 overflow-hidden">
          
          {/* Decorative gradient orbs */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[rgba(0,229,192,0.12)] to-[rgba(0,180,216,0.06)] rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-[rgba(0,229,192,0.06)] to-[rgba(0,180,216,0.03)] rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 pointer-events-none" />
          
          {/* Header */}
          <div className="relative px-6 py-5 border-b border-white/5">
            <button 
              onClick={onClose} 
              className="absolute top-4 right-4 p-2 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all duration-200"
              data-testid="close-modal-btn"
            >
              <X size={18} />
            </button>
            
            <div className="flex items-start gap-4">
              {/* Agent Icon with glow effect */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#00E5C0] to-[#00B4D8] rounded-2xl blur-lg opacity-40" />
                <div className="relative w-14 h-14 rounded-2xl bg-[#1A1D21] border border-[rgba(0,229,192,0.2)] flex items-center justify-center shadow-lg overflow-hidden">
                  <img
                    src={getWorkerIcon(outcomeCategory, agent?.name)}
                    alt={agent?.name || 'Agent'}
                    className="w-10 h-10 object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-bold text-white truncate pr-8">{agent?.name}</h2>
                {agent?.short_description && (
                  <p className="text-sm text-gray-400 mt-1 line-clamp-2">{agent.short_description}</p>
                )}
                
                {/* Accepted Input Types Badges */}
                {schema.acceptedInputTypes?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {schema.acceptedInputTypes.slice(0, 4).map((type, idx) => (
                      <InputTypeBadge key={idx} type={type} small />
                    ))}
                    {schema.acceptedInputTypes.length > 4 && (
                      <span className="text-xs text-gray-500 px-2 py-1">
                        +{schema.acceptedInputTypes.length - 4} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tab Switcher */}
          <div className="relative px-6 pt-3 pb-0 flex gap-1 border-b border-white/5">
            <button
              onClick={() => setActiveTab('run')}
              data-testid="tab-run-agent"
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors relative ${
                activeTab === 'run'
                  ? 'text-white'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <Play size={14} />
                Run Agent
              </span>
              {activeTab === 'run' && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00E5C0] rounded-full" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('history')}
              data-testid="tab-recent-runs"
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors relative ${
                activeTab === 'history'
                  ? 'text-white'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <Clock size={14} />
                Recent Runs
                {recentRuns.length > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-gray-400">{recentRuns.length}</span>
                )}
              </span>
              {activeTab === 'history' && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00E5C0] rounded-full" />
              )}
            </button>
          </div>

          {/* ── HISTORY TAB ── */}
          {activeTab === 'history' && (
            <div className="relative px-6 py-5" data-testid="recent-runs-panel">
              {loadingRuns ? (
                <div className="flex items-center justify-center py-12">
                  <CircleNotch size={24} className="text-[#00E5C0] animate-spin" />
                </div>
              ) : recentRuns.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-white/5 flex items-center justify-center">
                    <Clock size={22} className="text-gray-600" />
                  </div>
                  <p className="text-sm text-gray-500">No runs yet for this agent.</p>
                  <button
                    onClick={() => setActiveTab('run')}
                    className="mt-3 text-sm text-[#00E5C0] hover:underline"
                  >
                    Start your first run
                  </button>
                </div>
              ) : (
                <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                  {recentRuns.map((run) => {
                    const isExpanded = expandedRunId === run.session_id;
                    const statusMap = {
                      completed: { color: 'text-emerald-400', dot: 'bg-emerald-400', label: 'Success' },
                      failed: { color: 'text-red-400', dot: 'bg-red-400', label: 'Error' },
                      error: { color: 'text-red-400', dot: 'bg-red-400', label: 'Error' },
                      running: { color: 'text-blue-400', dot: 'bg-blue-400', label: 'Running' }
                    };
                    const st = statusMap[run.status] || { color: 'text-gray-400', dot: 'bg-gray-400', label: run.status || 'Queued' };
                    const inputSummary = summarise(run.input_payload?.text || run.input_payload, 90);
                    const outputSummary = summarise(run.output || run.summary, 120);

                    return (
                      <div
                        key={run.session_id}
                        className={`rounded-xl border transition-all duration-200 ${
                          isExpanded ? 'bg-white/[0.04] border-white/10' : 'bg-white/[0.02] border-white/5 hover:border-white/10'
                        }`}
                      >
                        {/* Run row header */}
                        <button
                          onClick={() => setExpandedRunId(isExpanded ? null : run.session_id)}
                          data-testid={`run-row-${run.session_id}`}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left"
                        >
                          {/* Status dot */}
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${st.dot}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-medium ${st.color}`}>{st.label}</span>
                              <span className="text-xs text-gray-600">·</span>
                              <span className="text-xs text-gray-500">{timeAgo(run.created_at)}</span>
                            </div>
                            {inputSummary && (
                              <p className="text-xs text-gray-400 mt-0.5 truncate">{inputSummary}</p>
                            )}
                          </div>
                          <ArrowRight size={14} className={`text-gray-600 transition-transform duration-200 flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
                        </button>

                        {/* Expanded detail */}
                        {isExpanded && (
                          <div className="px-4 pb-4 pt-1 border-t border-white/5 space-y-3">
                            {/* Input */}
                            {inputSummary && (
                              <div>
                                <p className="text-[10px] uppercase tracking-wider text-gray-600 mb-1">Input</p>
                                <p className="text-xs text-gray-400 bg-white/[0.03] rounded-lg px-3 py-2">{summarise(run.input_payload?.text || run.input_payload, 300)}</p>
                              </div>
                            )}
                            {/* Output */}
                            {outputSummary && (
                              <div>
                                <p className="text-[10px] uppercase tracking-wider text-gray-600 mb-1">Output</p>
                                <p className="text-xs text-gray-300 bg-white/[0.03] rounded-lg px-3 py-2 whitespace-pre-wrap" data-testid={`run-output-${run.session_id}`}>{outputSummary}</p>
                              </div>
                            )}
                            {/* Error */}
                            {run.error && (
                              <div>
                                <p className="text-[10px] uppercase tracking-wider text-red-500/70 mb-1">Error</p>
                                <p className="text-xs text-red-400/80 bg-red-500/5 rounded-lg px-3 py-2">{summarise(run.error, 200)}</p>
                              </div>
                            )}
                            {/* Actions */}
                            <div className="flex items-center gap-2 pt-1">
                              {run.input_payload?.text && (
                                <button
                                  onClick={() => handleUseInputs(run)}
                                  data-testid={`reuse-inputs-${run.session_id}`}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#00E5C0] bg-[rgba(0,229,192,0.08)] hover:bg-[rgba(0,229,192,0.15)] rounded-lg transition-colors"
                                >
                                  <ArrowClockwise size={12} />
                                  Use these inputs
                                </button>
                              )}
                              <button
                                onClick={() => handleViewRunDetails(run.session_id)}
                                data-testid={`view-run-${run.session_id}`}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-400 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                              >
                                <ArrowSquareOut size={12} />
                                Full details
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── RUN TAB (existing form) ── */}
          {activeTab === 'run' && (
          <>
          {/* Content */}
          <div className="relative px-6 py-6 space-y-5">
            {/* Error WarningCircle */}
            {error && (
              <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl animate-shake">
                <div className="p-1.5 rounded-lg bg-red-500/20">
                  <WarningCircle size={16} className="text-red-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-300">Error</p>
                  <p className="text-sm text-red-300/80 mt-0.5">{error}</p>
                </div>
                <button onClick={() => setError(null)} className="text-red-400/60 hover:text-red-400">
                  <X size={16} />
                </button>
              </div>
            )}

            {/* Output Preview (Fixed mode only) - Enhanced */}
            {schema.isFixedMode && schema.outputSchema?.fields && (
              <div className="p-4 bg-gradient-to-r from-[rgba(0,229,192,0.04)] to-[rgba(0,180,216,0.03)] border border-[rgba(0,229,192,0.1)] rounded-2xl">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkle size={16} className="text-[#00E5C0]" />
                  <h4 className="text-sm font-semibold text-white">Expected Output</h4>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {schema.outputSchema.fields.map((field, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2.5 bg-white/5 rounded-xl border border-white/5">
                      <CheckCircle size={14} className="text-emerald-400" />
                      <p className="text-xs font-medium text-white/90">{field.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Audio/Video UploadSimple - ONLY if accepted_inputs includes audio/video types */}
            {schema.showAudioUpload && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-white">
                  {schema.acceptsVideo ? <Video size={16} className="text-[#00E5C0]" /> : <Microphone size={16} className="text-[#00E5C0]" />}
                  {schema.acceptsVideo ? 'Audio/Video Recording' : 'Audio Recording'}
                  {schema.audioRequired && <span className="text-red-400">*</span>}
                </label>
                
                {uploadedAudio ? (
                  <div className="group relative p-4 bg-gradient-to-r from-[rgba(0,229,192,0.06)] to-[rgba(0,180,216,0.04)] border border-[rgba(0,229,192,0.15)] rounded-2xl transition-all duration-300 hover:border-[rgba(0,229,192,0.25)]">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00E5C0] to-[#00B4D8] flex items-center justify-center shadow-lg">
                        {uploadedAudio.type?.startsWith('video/') ? (
                          <Video size={22} className="text-[#0D0F11]" />
                        ) : (
                          <Microphone size={22} className="text-[#0D0F11]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{uploadedAudio.name}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{formatFileSize(uploadedAudio.size)}</p>
                      </div>
                      <button 
                        onClick={() => setUploadedAudio(null)} 
                        className="p-2 rounded-lg bg-white/5 text-gray-400 hover:bg-red-500/20 hover:text-red-400 transition-all"
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div 
                    onClick={() => audioInputRef.current?.click()}
                    className={`group relative p-8 border-2 border-dashed rounded-2xl cursor-pointer transition-all duration-300 text-center ${
                      schema.audioRequired 
                        ? 'border-[rgba(0,229,192,0.3)] bg-[rgba(0,229,192,0.03)] hover:border-[rgba(0,229,192,0.5)] hover:bg-[rgba(0,229,192,0.06)]' 
                        : 'border-white/10 hover:border-[rgba(0,229,192,0.3)] hover:bg-[rgba(0,229,192,0.03)]'
                    }`}
                  >
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-[rgba(0,229,192,0.15)] to-[rgba(0,180,216,0.1)] flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                      {schema.acceptsVideo ? (
                        <Video size={28} className="text-[#00E5C0]" />
                      ) : (
                        <Microphone size={28} className="text-[#00E5C0]" />
                      )}
                    </div>
                    <p className="text-sm font-medium text-white">
                      {schema.audioRequired 
                        ? (schema.acceptsVideo ? 'Upload recording (required)' : 'Upload audio (required)')
                        : 'Click or drag to upload'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1.5">
                      {schema.acceptsVideo ? 'MP3, WAV, M4A, MP4, MOV, WebM • Max 100MB' : 'MP3, WAV, M4A • Max 100MB'}
                    </p>
                  </div>
                )}
                
                <input
                  ref={audioInputRef}
                  type="file"
                  accept={schema.acceptsVideo 
                    ? `${INPUT_CONFIG.audio_file?.accept || '.mp3,.wav,.m4a'},${INPUT_CONFIG.video_file?.accept || '.mp4,.mov,.webm'}`
                    : INPUT_CONFIG.audio_file?.accept || '.mp3,.wav,.m4a'}
                  onChange={handleAudioSelect}
                  className="hidden"
                />
              </div>
            )}

            {/* File UploadSimple - Enhanced Design */}
            {schema.showFileUpload && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-white">
                  <UploadSimple size={16} className="text-blue-400" />
                  Document UploadSimple
                  {schema.fileRequired && <span className="text-red-400">*</span>}
                </label>
                
                {uploadedFile ? (
                  <div className="group relative p-4 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-2xl transition-all duration-300 hover:border-blue-500/30">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-lg">
                        <FileText size={22} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{uploadedFile.name}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{formatFileSize(uploadedFile.size)}</p>
                      </div>
                      <button 
                        onClick={() => setUploadedFile(null)} 
                        className="p-2 rounded-lg bg-white/5 text-gray-400 hover:bg-red-500/20 hover:text-red-400 transition-all"
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="group relative p-8 border-2 border-dashed border-white/10 rounded-2xl cursor-pointer hover:border-blue-500/40 hover:bg-blue-500/5 transition-all duration-300 text-center"
                  >
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                      <UploadSimple size={28} className="text-blue-400" />
                    </div>
                    <p className="text-sm font-medium text-white">Click or drag to upload</p>
                    <p className="text-xs text-gray-500 mt-1.5">PDF, Word, Excel, CSV, Images • Max 50MB</p>
                  </div>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={schema.acceptedFileExtensions}
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            )}

            {/* Reference Material (runtime) — adaptive based on worker config */}
            {(schema.showRefText || schema.showRefFile) && (
              <div className="space-y-2" data-testid="ref-material-section">
                <label className="flex items-center gap-2 text-sm font-medium text-white">
                  <FileText size={16} className="text-amber-400" />
                  Reference Material
                  {schema.refRequired && <span className="text-red-400 text-xs">*</span>}
                  {!schema.refRequired && <span className="text-gray-500 text-xs font-normal">(optional)</span>}
                </label>
                <p className="text-xs text-gray-500 -mt-1">
                  Provide rubric, checklist, or evaluation criteria for this analysis.
                </p>

                {schema.showRefText && (
                  <textarea
                    value={refText}
                    onChange={(e) => setRefText(e.target.value)}
                    rows={3}
                    placeholder="Paste rubric, marking guide, or evaluation criteria..."
                    data-testid="modal-ref-text-input"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[rgba(245,158,11,0.4)] focus:border-[rgba(245,158,11,0.4)] transition-all resize-none"
                  />
                )}

                {schema.showRefFile && (
                  <>
                    {refFile ? (
                      <div className="flex items-center gap-3 px-4 py-3 bg-amber-500/5 border border-amber-500/15 rounded-xl">
                        <FileText size={18} className="text-amber-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-white truncate">{refFile.name}</p>
                          <p className="text-[10px] text-gray-500">{(refFile.size / 1024).toFixed(1)} KB</p>
                        </div>
                        <button onClick={() => setRefFile(null)} className="text-gray-400 hover:text-red-400 transition-colors" data-testid="modal-remove-ref-file">
                          <Trash size={14} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => document.getElementById('modal-ref-file-input')?.click()}
                        data-testid="modal-ref-file-upload-btn"
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-dashed border-white/10 rounded-xl text-xs text-gray-400 hover:border-amber-500/30 hover:text-amber-400 hover:bg-amber-500/5 transition-all"
                      >
                        <UploadSimple size={14} />
                        Upload reference file (PDF, DOCX, TXT)
                      </button>
                    )}
                    <input
                      id="modal-ref-file-input"
                      type="file"
                      className="hidden"
                      accept=".pdf,.doc,.docx,.txt,.csv,.xlsx"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) setRefFile(f); }}
                    />
                  </>
                )}
              </div>
            )}

            {/* URL Input - Enhanced */}
            {schema.showUrlInput && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-white">
                  <Globe size={16} className="text-cyan-400" />
                  URL
                  {schema.urlRequired && <span className="text-red-400">*</span>}
                </label>
                <div className="relative">
                  <input
                    type="url"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="https://example.com"
                    className="w-full px-4 py-3 pl-11 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[rgba(0,229,192,0.4)] focus:border-[rgba(0,229,192,0.4)] transition-all"
                  />
                  <LinkIcon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                </div>
              </div>
            )}

            {/* Webhook/Automation Config - Enhanced */}
            {schema.showWebhookConfig && (
              <div className="p-5 bg-gradient-to-r from-orange-500/5 to-amber-500/5 border border-orange-500/15 rounded-2xl">
                <div className="flex items-start gap-4 min-w-0">
                  <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center shadow-lg flex-shrink-0">
                    <WifiHigh size={22} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-white mb-1">Webhook Trigger</h4>
                    <p className="text-xs text-gray-400 mb-3">
                      This agent is triggered via webhook. Configure the webhook URL in agent settings.
                    </p>
                    <div className="p-3 bg-[rgba(0,0,0,0.3)] rounded-lg overflow-hidden">
                      <p className="text-xs text-gray-500 mb-1">Webhook Endpoint</p>
                      <div className="flex items-center gap-2 min-w-0">
                        <code className="text-xs text-orange-300 font-mono flex-1 truncate block min-w-0">
                          {BACKEND_URL}/api/webhook/{agent?.id || 'agent_id'}/trigger
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`${BACKEND_URL}/api/webhook/${agent?.id}/trigger`);
                          }}
                          className="p-1.5 text-gray-400 hover:text-white"
                          title="Copy URL"
                        >
                          <ArrowSquareOut size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Customer Details Form - for quote/document agents */}
            {schema.showCustomerForm && (
              <div className="p-4 bg-[rgba(0,229,192,0.03)] border border-[rgba(0,229,192,0.1)] rounded-xl">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <FileText size={16} className="text-[#00E5C0]" />
                  Customer Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={customerDetails.name}
                    onChange={(e) => setCustomerDetails({ ...customerDetails, name: e.target.value })}
                    placeholder="Customer Name *"
                    className="px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#00E5C0]"
                  />
                  <input
                    type="text"
                    value={customerDetails.company}
                    onChange={(e) => setCustomerDetails({ ...customerDetails, company: e.target.value })}
                    placeholder="Company (optional)"
                    className="px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#00E5C0]"
                  />
                  <input
                    type="email"
                    value={customerDetails.email}
                    onChange={(e) => setCustomerDetails({ ...customerDetails, email: e.target.value })}
                    placeholder="Email"
                    className="px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#00E5C0]"
                  />
                  <input
                    type="tel"
                    value={customerDetails.phone}
                    onChange={(e) => setCustomerDetails({ ...customerDetails, phone: e.target.value })}
                    placeholder="Phone"
                    className="px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#00E5C0]"
                  />
                  <input
                    type="text"
                    value={customerDetails.address}
                    onChange={(e) => setCustomerDetails({ ...customerDetails, address: e.target.value })}
                    placeholder="Address"
                    className="px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#00E5C0] md:col-span-2"
                  />
                </div>
              </div>
            )}

            {/* Services Selector - for quote-type agents */}
            {schema.showServicesSelector && (
              <div className="p-4 bg-[rgba(16,185,129,0.05)] border border-[rgba(16,185,129,0.15)] rounded-xl">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <GearSix size={16} className="text-emerald-400" />
                  Select Services
                </h4>
                {availableServices.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                    {availableServices.map(service => {
                      const isSelected = selectedServices.find(s => s.id === service.id);
                      return (
                        <div
                          key={service.id}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedServices(selectedServices.filter(s => s.id !== service.id));
                            } else {
                              setSelectedServices([...selectedServices, { ...service, quantity: 1 }]);
                            }
                          }}
                          className={`p-2.5 rounded-lg border cursor-pointer transition-all ${
                            isSelected 
                              ? 'bg-emerald-500/10 border-emerald-500/50' 
                              : 'bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-white text-sm font-medium">{service.name}</p>
                              <p className="text-gray-500 text-xs">
                                {businessSettings?.currency?.symbol || '$'}{service.unit_price?.toFixed(2)} / {service.unit_type}
                              </p>
                            </div>
                            {isSelected && (
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  value={isSelected.quantity}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setSelectedServices(selectedServices.map(s => 
                                      s.id === service.id ? { ...s, quantity: Math.max(1, parseInt(e.target.value) || 1) } : s
                                    ));
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  min="1"
                                  className="w-14 px-2 py-1 bg-[rgba(0,0,0,0.3)] border border-[rgba(255,255,255,0.1)] rounded text-white text-xs text-center"
                                />
                                <CheckCircle size={16} className="text-emerald-400" />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No services configured. Add services in agent training data.</p>
                )}
              </div>
            )}

            {/* Materials Selector - for quote-type agents */}
            {schema.showMaterialsSelector && (
              <div className="p-4 bg-[rgba(245,158,11,0.05)] border border-[rgba(245,158,11,0.15)] rounded-xl">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <FileXls size={16} className="text-amber-400" />
                  Select Materials
                </h4>
                {availableMaterials.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                    {availableMaterials.map(material => {
                      const isSelected = selectedMaterials.find(m => m.id === material.id);
                      const sellPrice = material.cost * (1 + (material.markup_percent || 0) / 100);
                      return (
                        <div
                          key={material.id}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedMaterials(selectedMaterials.filter(m => m.id !== material.id));
                            } else {
                              setSelectedMaterials([...selectedMaterials, { ...material, quantity: 1 }]);
                            }
                          }}
                          className={`p-2.5 rounded-lg border cursor-pointer transition-all ${
                            isSelected 
                              ? 'bg-amber-500/10 border-amber-500/50' 
                              : 'bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-white text-sm font-medium">{material.name}</p>
                              <p className="text-gray-500 text-xs">
                                {businessSettings?.currency?.symbol || '$'}{sellPrice.toFixed(2)} / {material.unit}
                              </p>
                            </div>
                            {isSelected && (
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  value={isSelected.quantity}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setSelectedMaterials(selectedMaterials.map(m => 
                                      m.id === material.id ? { ...m, quantity: Math.max(1, parseInt(e.target.value) || 1) } : m
                                    ));
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  min="1"
                                  className="w-14 px-2 py-1 bg-[rgba(0,0,0,0.3)] border border-[rgba(255,255,255,0.1)] rounded text-white text-xs text-center"
                                />
                                <CheckCircle size={16} className="text-amber-400" />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No materials configured. Add materials in agent training data.</p>
                )}
              </div>
            )}

            {/* Text Input - Enhanced Design */}
            {schema.showTextInput && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-white">
                  <ChatText size={16} className="text-[#00E5C0]" />
                  {schema.isFixedMode ? 'Additional Notes' : 'Instructions'}
                  {schema.textRequired && <span className="text-red-400">*</span>}
                </label>
                <div className="relative">
                  <textarea
                    value={userText}
                    onChange={(e) => setUserText(e.target.value)}
                    placeholder={
                      schema.isFixedMode 
                        ? 'Any specific aspects to focus on or additional context...' 
                        : 'Describe what you want the agent to do...'
                    }
                    rows={schema.isFixedMode ? 3 : 4}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[rgba(0,229,192,0.4)] focus:border-[rgba(0,229,192,0.4)] resize-none transition-all"
                  />
                  {/* Character count indicator */}
                  <div className="absolute bottom-3 right-3 text-xs text-gray-500">
                    {userText.length > 0 && `${userText.length} chars`}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer - Modern CTA */}
          <div className="relative px-6 py-5 border-t border-white/5 bg-gradient-to-t from-slate-900/50 to-transparent">
            <div className="flex items-center justify-between gap-4">
              {/* Keyboard shortcut hint */}
              <div className="hidden sm:flex items-center gap-2 text-xs text-gray-500">
                <kbd className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded text-[10px]">⌘</kbd>
                <span>+</span>
                <kbd className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded text-[10px]">Enter</kbd>
                <span>to run</span>
              </div>
              
              <div className="flex items-center gap-3 ml-auto">
                <button
                  onClick={onClose}
                  disabled={loading || uploading}
                  className="px-5 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-xl transition-all duration-200 disabled:opacity-50"
                  data-testid="cancel-run-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading || uploading || !canSubmit}
                  className="group relative px-6 py-2.5 bg-[#00E5C0] hover:bg-[#00CCB0] text-[#0D0F11] font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none flex items-center gap-2 transition-all duration-300 overflow-hidden"
                  data-testid="run-agent-btn"
                >
                  <span className="flex items-center gap-2">
                    {loading || uploading ? (
                      <>
                        <ModernSpinner size={18} />
                        <span>{uploading ? 'Uploading...' : 'Running...'}</span>
                      </>
                    ) : (
                      <>
                        {React.createElement(schema.ctaIcon || Play, { size: 18 })}
                        <span>{schema.ctaText}</span>
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
                      </>
                    )}
                  </span>
                </button>
              </div>
            </div>
          </div>
          </>
          )}
        </div>
      </div>
    </>
  );
};

export default RunAgentModal;
